import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {Link, useNavigate} from 'react-router-dom';
import { Card, CardBody, Col, Row, Input, Alert, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import FeatherIcon from 'feather-icons-react';
import { useTranslation } from 'react-i18next';
import DeleteModal from '../../../Components/Common/DeleteModal';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';
import api, {handleApiError} from '../../../helpers/api';
import {useDispatch} from "react-redux";

// Custom CSS
const customStyles = `
.card {
  transition: transform 0.3s ease;
  max-width: 300px;
  width: 100%;
  height: 350px; /* Fixed card height */
}
.card:hover {
  transform: translateY(-5px);
}
.filter-container {
  display: flex;
  flex-direction: row;
  gap: 1rem;
  align-items: center;
  flex-wrap: nowrap;
  overflow-x: auto;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
}
.filter-container .form-control, .filter-container .form-select {
  border-radius: 4px;
  border: 1px solid #ced4da;
  min-width: 140px;
  font-size: 0.9rem;
  padding: 0.5rem 1rem;
}
.filter-container .form-control:focus, .filter-container .form-select:focus {
  border-color: #0ab39c;
  box-shadow: 0 0 0 0.2rem rgba(10, 179, 156, 0.25);
}
.action-btn {
  font-size: 0.85rem;
  padding: 0.4rem 0.8rem;
  transition: background-color 0.3s ease, transform 0.2s ease;
}
.action-btn:hover {
  transform: translateY(-2px);
}
.dropdown-menu {
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border: none;
}
.dropdown-item {
  font-size: 0.85rem;
  padding: 0.5rem 1rem;
  color: #2a4966;
}
.dropdown-item:hover {
  background-color: #f1f5f7;
  color: #0ab39c;
}
.dropdown-item i {
  margin-right: 0.5rem;
}
.avatar-sm {
  width: 2.5rem;
  height: 2.5rem;
  line-height: 2.5rem;
  font-size: 1rem;
}
.pagination-separated .page-link {
  border-radius: 4px;
  margin: 0 0.2rem;
}
.pagination-separated .page-item.active .page-link {
  background-color: #0ab39c;
  border-color: #0ab39c;
}
.text-wrap {
  white-space: normal;
  word-break: break-word;
  max-width: 100%;
}
.description-text {
  white-space: normal; /* Allow wrapping */
  word-break: break-word;
  max-width: 100%;
  overflow: hidden;
}
.tags-text {
  white-space: normal; /* Allow wrapping */
  word-break: break-word;
  max-width: 100%;
  overflow: hidden;
}
@media (max-width: 768px) {
  .filter-container {
    flex-wrap: nowrap;
    overflow-x: auto;
  }
  .filter-container .form-control, .filter-container .form-select {
    min-width: 120px;
    font-size: 0.85rem;
  }
  .card {
    max-width: 100%;
    height: 450px; /* Fixed card height for mobile */
  }
}
`;

const List = () => {
    const { t } = useTranslation();
    const [resources, setResources] = useState([]);
    const [filteredResources, setFilteredResources] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleteModal, setDeleteModal] = useState(false);
    const [selectedResource, setSelectedResource] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [filterCategory, setFilterCategory] = useState('all');
    const [sortOrder, setSortOrder] = useState('desc');
    const [sortField, setSortField] = useState('publicationDate');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 8;
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const fetchResources = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await api.get('/ressource', { withCredentials: true });
            setResources(response.data);
            setFilteredResources(response.data);
            setIsLoading(false);
        } catch (err) {
            if (err.response.data?.status === 401 || err.response.data?.status === 403) {
                await handleApiError(err, dispatch, navigate);
            } else {
                setError(t('resource_list.error.fetch_failed'));
                setIsLoading(false);
                toast.error(t('resource_list.toasts.fetch_failed'));
            }
        }
    }, [t, dispatch, navigate]);

    useEffect(() => {
        fetchResources();
    }, [fetchResources]);

    // Filter and Sort Resources
    useEffect(() => {
        let updatedResources = [...resources];

        // Apply search filter
        if (searchQuery) {
            updatedResources = updatedResources.filter(resource =>
                (resource.titre?.toLowerCase().includes(searchQuery.toLowerCase()) || '') ||
                (resource.description?.replace(/<[^>]+>/g, '').toLowerCase().includes(searchQuery.toLowerCase()) || '') ||
                (resource.categorie?.toLowerCase().includes(searchQuery.toLowerCase()) || '') ||
                (resource.tags || []).some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
            );
        }

        // Apply type filter
        if (filterType !== 'all') {
            updatedResources = updatedResources.filter(resource => resource.type === filterType);
        }

        // Apply category filter
        if (filterCategory !== 'all') {
            updatedResources = updatedResources.filter(resource => resource.categorie === filterCategory);
        }

        // Apply sort order
        updatedResources.sort((a, b) => {
            if (sortField === 'publicationDate') {
                const dateA = new Date(a.publicationDate || Date.now());
                const dateB = new Date(b.publicationDate || Date.now());
                return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
            } else {
                return sortOrder === 'desc' ? b.vues - a.vues : a.vues - b.vues;
            }
        });

        setFilteredResources(updatedResources);
    }, [resources, searchQuery, filterType, filterCategory, sortOrder, sortField]);

    // Handle Delete
    const handleDeleteResource = async () => {
        try {
            await api.delete(`/ressource/${selectedResource._id}`, { withCredentials: true });
            toast.success(t('resource_list.toasts.delete_success'));
            setDeleteModal(false);
            fetchResources();
        } catch (err) {
            if (err.response.data?.status === 401 || err.response.data?.status === 403) {
                await handleApiError(err, dispatch, navigate);
            } else {
                toast.error(t('resource_list.toasts.delete_failed'));
            }
        }
    };

    // Handle Delete Click
    const handleDeleteClick = (resource) => {
        setSelectedResource(resource);
        setDeleteModal(true);
    };

    // Get unique categories for filter
    const categories = useMemo(() => {
        const unique = [...new Set(resources.map(resource => resource.categorie).filter(Boolean))];
        return ['all', ...unique];
    }, [resources]);

    // Pagination
    const paginatedResources = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        const end = start + itemsPerPage;
        return filteredResources.slice(start, end);
    }, [filteredResources, currentPage]);

    const totalPages = Math.ceil(filteredResources.length / itemsPerPage);

    // Type to badge and image background class mapping
    const typeStyles = {
        PDF: { badge: 'badge-soft-primary', imgBg: 'primary' },
        article: { badge: 'badge-soft-info', imgBg: 'info' },
        vidéo: { badge: 'badge-soft-success', imgBg: 'success' },
    };

    // Format tags
    const formatTags = (tags) => {
        if (!tags || tags.length === 0) return t('resource_list.tags_none');
        if (tags.length <= 3) return tags.join(', ');
        return `${tags.slice(0, 3).join(', ')}, ${t('resource_list.tags_etc')}`;
    };

    // Truncate description
    const truncateDescription = (description) => {
        if (!description) return t('resource_list.no_description');
        const cleanDescription = DOMPurify.sanitize(description).replace(/<[^>]+>/g, '');
        if (cleanDescription.length > 100) {
            return `${cleanDescription.substring(0, 100)}...`;
        }
        return cleanDescription;
    };

    return (
        <React.Fragment>
            <style>{customStyles}</style>
            <ToastContainer closeButton={false} limit={1} />
            <DeleteModal
                show={deleteModal}
                onDeleteClick={handleDeleteResource}
                onCloseClick={() => setDeleteModal(false)}
            />
            <Row className="g-4 mb-3">
                <Col sm="auto">
                    <Link to="/apps-resource-create" className="btn btn-success">
                        <i className="ri-add-line align-bottom me-1"></i> {t('resource_list.add_new')}
                    </Link>
                </Col>
                <Col sm={6} className="ms-auto" style={{width:'60%'}}>
                    <div className="filter-container hstack gap-2 justify-content-sm-end">
                        <div className="search-box position-relative" style={{paddingLeft:'27px !important'}}>
                            <Input
                                type="text"
                                className="form-control"
                                placeholder={t('resource_list.search_placeholder')}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            <i className="ri-search-line search-icon position-absolute top-50 start-0 translate-middle-y ms-2"></i>
                        </div>
                        <Input
                            type="select"
                            className="form-select"
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                        >
                            <option value="all">{t('resource_list.filter_type.all')}</option>
                            <option value="PDF">{t('resource_list.filter_type.pdf')}</option>
                            <option value="article">{t('resource_list.filter_type.article')}</option>
                            <option value="vidéo">{t('resource_list.filter_type.video')}</option>
                        </Input>
                        <Input
                            type="select"
                            className="form-select"
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                        >
                            <option value="all">{t('resource_list.filter_category.all')}</option>
                            {categories.map(category => (
                                <option key={category} value={category}>
                                    {category === 'all' ? t('resource_list.filter_category.all') : category}
                                </option>
                            ))}
                        </Input>
                        <Input
                            type="select"
                            className="form-select"
                            value={`${sortField}-${sortOrder}`}
                            onChange={(e) => {
                                const [field, order] = e.target.value.split('-');
                                setSortField(field);
                                setSortOrder(order);
                            }}
                        >
                            <option value="publicationDate-desc">{t('resource_list.sort.newest')}</option>
                            <option value="publicationDate-asc">{t('resource_list.sort.oldest')}</option>
                            <option value="vues-desc">{t('resource_list.sort.most_viewed')}</option>
                            <option value="vues-asc">{t('resource_list.sort.least_viewed')}</option>
                        </Input>
                    </div>
                </Col>
            </Row>
            {isLoading ? (
                <div className="text-center py-4">
                    <i className="ri-loader-2-line fs-24 text-primary spin"></i>
                </div>
            ) : error ? (
                <Alert color="danger">{error}</Alert>
            ) : (
                <Row>
                    {paginatedResources.map((resource, index) => (
                        <Col xxl={3} sm={6} key={index}>
                            <Card className="card-height-100">
                                <CardBody>
                                    <div className="d-flex flex-column h-100">
                                        <div className="d-flex">
                                            <div className="flex-grow-1">
                                                <p className="text-muted mb-4">{new Date(resource.publicationDate).toLocaleDateString()}</p>
                                            </div>
                                            <div className="flex-shrink-0">
                                                <UncontrolledDropdown direction="start">
                                                    <DropdownToggle tag="button" className="btn btn-link text-muted p-1 mt-n2 py-0 text-decoration-none fs784-15 shadow-none action-btn">
                                                        <FeatherIcon icon="more-horizontal" className="icon-sm" />
                                                    </DropdownToggle>
                                                    <DropdownMenu className="dropdown-menu-end">
                                                        <DropdownItem tag={Link} to={`/apps-resource-overview/${resource._id}`}>
                                                            <i className="ri-eye-fill align-bottom me-2 text-muted"></i> {t('resource_list.actions.view')}
                                                        </DropdownItem>
                                                        <DropdownItem tag={Link} to={`/apps-resource-edit/${resource._id}`}>
                                                            <i className="ri-pencil-fill align-bottom me-2 text-muted"></i> {t('resource_list.actions.edit')}
                                                        </DropdownItem>
                                                        <DropdownItem divider />
                                                        <DropdownItem onClick={() => handleDeleteClick(resource)}>
                                                            <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i> {t('resource_list.actions.delete')}
                                                        </DropdownItem>
                                                    </DropdownMenu>
                                                </UncontrolledDropdown>
                                            </div>
                                        </div>
                                        <div className="d-flex mb-2">
                                            <div className="flex-shrink-0 me-3">
                                                <div className="avatar-sm">
                          <span className={`avatar-title rounded p-2 bg-soft-${typeStyles[resource.type]?.imgBg || 'secondary'}`}>
                            <img src={resource.openGraphImage} alt="" className="img-fluid" />
                          </span>
                                                </div>
                                            </div>
                                            <div className="flex-grow-1">
                                                <h5 className="mb-1 fs-15 text-wrap">
                                                    <Link to={`/apps-resource-overview/${resource._id}`} className="text-dark">{resource.titre}</Link>
                                                </h5>
                                                <p className="text-muted description-text mb-3">
                                                    {truncateDescription(resource.description)}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="mt-auto">
                                            <div className="d-flex mb-2">
                                                <div className="flex-grow-1">
                                                    <div>{t('resource_list.type_label')}</div>
                                                </div>
                                                <div className="flex-shrink-0">
                                                    <div className={`fs-12 badge ${typeStyles[resource.type]?.badge || 'badge-soft-secondary'}`}>
                                                        {t(`resource_list.types.${resource.type}`) || resource.type}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="d-flex mb-2">
                                                <div className="flex-grow-1">
                                                    <div>{t('resource_list.category_label')}</div>
                                                </div>
                                                <div className="flex-shrink-0">
                                                    <div className="fs-12 badge badge-soft-secondary">
                                                        {resource.categorie || t('resource_list.uncategorized')}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="d-flex mb-2">
                                                <div className="flex-grow-1">
                                                    <div>{t('resource_list.tags_label')}</div>
                                                </div>
                                                <div className="flex-shrink-0">
                                                    <div className="text-muted tags-text">{formatTags(resource.tags)}</div>
                                                </div>
                                            </div>
                                            <div className="d-flex mb-2">
                                                <div className="flex-grow-1">
                                                    <div>{t('resource_list.views_label')}</div>
                                                </div>
                                                <div className="flex-shrink-0">
                                                    <div>{resource.vues.toLocaleString()} {t('resource_list.views')}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}
            <Row className="g-0 text-center text-sm-start align-items-center mb-4">
                <Col sm={6}>
                    <div>
                        <p className="mb-sm-0 text-muted">
                            Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredResources.length)} of {filteredResources.length} resources
                        </p>
                    </div>
                </Col>
                <Col sm={6}>
                    <ul className="pagination pagination-separated justify-content-center justify-content-sm-end mb-sm-0">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <Link to="#" className="page-link" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}>
                                {t('resource_list.pagination.previous')}
                            </Link>
                        </li>
                        {[...Array(totalPages)].map((_, i) => (
                            <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                                <Link to="#" className="page-link" onClick={() => setCurrentPage(i + 1)}>
                                    {i + 1}
                                </Link>
                            </li>
                        ))}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <Link to="#" className="page-link" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}>
                                {t('resource_list.pagination.next')}
                            </Link>
                        </li>
                    </ul>
                </Col>
            </Row>
        </React.Fragment>
    );
};

export default List;